import React, { useEffect } from 'react';
import { DefaultSeo } from 'next-seo';
import Head from 'next/head';

import seo from '../src/config/seo_en.json';
import './_app.scss';
import colors from '../src/styles/_colors-for-js.module.scss';
import { init } from '../src/utils/sentry';

import { IncomingMessage } from 'http';
import { NextPageContext } from 'next';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import myTheme from '../styles/theme';
import fonts from '../styles/font-face';

import { RtlProvider } from '../src/components/rtl-provider/rtl-provider';
import { useRouter } from 'next/router';
import * as locales from '../public/assets/lang/index';
import { IntlProvider } from 'react-intl';
import { GTMPageView } from '../src/utils/gtm';

// @todo typescript server and share types from middlewares
interface IReq extends IncomingMessage {
  lang?: any;
  ipInfo?: any;
  // isMobile?: isMobileResult;
  initialReduxData?: any;
  initialApiData?: any;
  appUser?: any;
  cookies?: any;
  clientIp?: any;
  messages?: any;
  appConfig?: any;
  envConfig?: any;
  statusCode?: number;
}

export interface IPageContext extends NextPageContext {
  req: IReq;
  // reduxStore?: Store<CombinedStateType>;
}

import 'slick-carousel/slick/slick.css';
import '../src/styles/slick/slick-theme.css';
// import 'slick-carousel/slick/slick-theme.css';

const theme = extendTheme({
  ...myTheme,
  colors: {
    // custom colors can be defined like this.
    imColor: {
      primary: colors['theme-default-primary'],
      'primary-dark': colors['theme-default-primary-dark'],
      'primary-light': colors['theme-default-primary-light'],
      secondary: colors['theme-default-secondary'],
      black: colors['theme-default-black'],
      gray: colors['theme-default-gray'],
    } as any,
  },
});

init();

function MyApp(props: any) {
  const { Component, pageProps, err }: any = props;
  const router = useRouter();
  const { locale = 'en', defaultLocale, pathname, events } = router;
  const localeCopy = (locales as any)[locale as string];
  const messages = { ...localeCopy['global'], ...localeCopy[pathname] };

  // Initiate GTM
  useEffect(() => {
    const handleRouteChange = (url: string) => GTMPageView(url);
    events.on('routeChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  return (
    <>
      <Head>
        <meta content='IE=edge' httpEquiv='X-UA-Compatible' />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes'
        />
        <meta name='screen-orientation' content='portrait' />
        <meta name='theme-color' content='#f4f4f4' />
        <meta name='application-name' content={`Insurance Market`} />
        <meta name='description' content={`Insurance Market`} />

        {/* ========== preload links START ========== */}

        <link
          rel='preload'
          as='image'
          href='/assets/img/yacht/banner/background.png'
        />

        {/* ========== preload links END ========== */}

        <link rel='icon' href='/assets/img/favicon_package/favicon.ico' />
        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href='/assets/img/favicon_package/favicon-16x16.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='32x32'
          href='/assets/img/favicon_package/favicon-32x32.png'
        />
        <link
          rel='apple-touch-icon'
          sizes='152x152'
          href={`/assets/img/favicon_package/apple-touch-icon.png`}
        />
        <link
          rel='apple-touch-icon'
          sizes='167x167'
          href={`/assets/img/favicon_package/apple-touch-icon.png`}
        />
        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href={`/assets/img/favicon_package/apple-touch-icon.png`}
        />

        <meta name='msapplication-tooltip' content={`Insurance Market`} />
        <meta name='msapplication-navbutton-color' content='#2196f3' />
        <meta name='msapplication-TileColor' content='#f4f4f4' />
        <meta
          name='msapplication-TileImage'
          content={`/assets/img/favicon_package/mstile-150x150.png`}
        />
        <meta name='apple-mobile-web-app-title' content={`Insurance Market`} />
        <meta name='apple-mobile-web-app-capable' content={`yes`} />
        <meta
          name='apple-mobile-web-app-status-bar-style'
          content={`black-translucent`}
        />

        <link rel='manifest' href={'/assets/manifest.json'} />
        <title>{seo.default.title}</title>
      </Head>
      <DefaultSeo {...seo.default} />
      <ChakraProvider theme={theme}>
        <Global styles={fonts} />
        <RtlProvider lang={locale}>
          <IntlProvider
            locale={locale}
            defaultLocale={defaultLocale}
            messages={messages}
          >
            <Component {...pageProps} err={err} />
          </IntlProvider>
        </RtlProvider>
      </ChakraProvider>
    </>
  );
}

export default MyApp;
